import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Sparkles, CalendarPlus } from "lucide-react";
import { toast } from "sonner";

const years = (() => { const out = []; for (let y = 2024; y <= 2028; y++) out.push(y); return out; })();

export default function PublicHolidaysAdmin() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/public-holidays?year=${year}`);
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const seedIreland = async () => {
    if (year !== 2026 && year !== 2027) return toast.error("Pre-seed available for 2026/2027 only — add others manually.");
    setBusy(true);
    try {
      const r = await api.post(`/public-holidays/seed/ireland/${year}`);
      toast.success(`Inserted ${r.data.inserted}, skipped ${r.data.skipped}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const add = async () => {
    if (!newDate || !newName) return toast.error("Date and name required");
    setBusy(true);
    try {
      await api.post(`/public-holidays`, { date: newDate, name: newName, country: "IE" });
      toast.success("Added");
      setNewDate(""); setNewName("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this public holiday?")) return;
    try {
      await api.delete(`/public-holidays/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="border-border shadow-none" data-testid="public-holidays-admin">
      <CardContent className="p-6 space-y-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-9 w-32" data-testid="ph-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={seedIreland} disabled={busy} className="h-9" data-testid="ph-seed-ireland">
            <Sparkles className="w-4 h-4 mr-1.5" /> Seed Ireland {year}
          </Button>
          <div className="ml-auto text-xs text-muted-foreground">{rows.length} holidays</div>
        </div>

        <div className="border rounded-md divide-y">
          {rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No public holidays defined for {year}.
            </div>
          ) : rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`ph-row-${r.date}`}>
              <span className="font-mono text-xs text-muted-foreground w-28">{r.date}</span>
              <span className="font-medium flex-1 truncate">{r.name}</span>
              <Button size="icon" variant="ghost" onClick={() => remove(r.id)} title="Delete">
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-3 border-t pt-4">
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="h-9 w-44" data-testid="ph-new-date" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Name</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Company shut-down" className="h-9" data-testid="ph-new-name" />
          </div>
          <Button onClick={add} disabled={busy} className="h-9" data-testid="ph-add">
            <CalendarPlus className="w-4 h-4 mr-1.5" /> Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
